import { useState, useEffect } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { BiChevronsLeft } from 'react-icons/bi'
import { toast } from 'react-toastify'
import ToastMessage from '../ToastMessage'
import StakeModal from './StakeModal'
import UnstakeModal from './UnstakeModal'
import WithdrawRow from './WithdrawRow'
import Value from './Value'
import {
  TotalValueLockedH4,
  StakeCol,
  StakeWrapperFlex,
  Icon,
  Icons,
  LpIcons,
  StakeButtons,
  StakeDescription,
  StakeNumber,
  ShareOfPool,
  ButtonFlex,
  WithdrawDiv,
  WithdrawHeading,
  WithdrawAmount,
  WithdrawAction,
} from './Styled'
import Button from '../Button'
import {
  useActiveWeb3React,
  useDTOPrice,
  useTVL,
  ApprovalState,
  useApproveCallback,
  useTokenBalance,
  usePendingRewards,
  useTotalSupply,
  useTokenContract,
} from '../../hooks'
import { formatNumber, fromWei } from '../../utils'
import config from '../../config.json'
import LockInfo from '../../type/LockInfo'
import ParaLogo from '../../assets/images/para-logo-mobile.svg'

function SingleStake(): JSX.Element {
  // @ts-ignore
  const { slug } = useParams()

  const [isLoading, setLoading] = useState(false)
  const [isClaiming, setClaiming] = useState(false)
  const [needApprove, setNeedApprove] = useState(true)

  const [showStakeModal, setShowStakeModal] = useState(false)
  const [showUnstakeModal, setShowUnstakeModal] = useState(false)
  const [earned, setEarned] = useState(0)
  const [tvl, setTVL] = useState(0)
  const [staked, setStaked] = useState(0)
  const [shareOfPool, setShareOfPool] = useState(0)
  const [lockInfo, setLockInfo] = useState<LockInfo[]>([])

  const { account, chainId } = useActiveWeb3React()
  const networkId = chainId ?? Number(process.env.REACT_APP_CHAIN_ID)
  const pools = config.pools[networkId]
  // @ts-ignore
  const pool = pools.find(p => p.slug === slug)

  const earnedBalanceCallback = usePendingRewards(pool?.contract, account)
  const [approval, approveCallback] = useApproveCallback(pool?.lpAddress, pool?.contract)
  const stakedBalanceCallback = useTokenBalance(pool?.contract, account)
  const rewardCallback = useTokenContract(pool?.contract)
  const tokenContract = useTokenContract(pool?.lpAddress)

  const totalSupplyCallback = useTotalSupply(pool?.contract)
  const dtoPriceCallback = useDTOPrice()
  const tvlCallback = useTVL(pool.id)

  const fetchStakedBalance = async () => {
    try {
      const _stakedBalance = await stakedBalanceCallback()
      const _totalSupply = await totalSupplyCallback()
      setStaked(_stakedBalance)
      if (_stakedBalance === 0 || _totalSupply === 0) {
        setShareOfPool(0)
      } else {
        setShareOfPool((_stakedBalance / _totalSupply) * 100)
      }
    } catch (error: any) {
      console.error(error)
    }
  }

  const fetchPendingRewards = async () => {
    try {
      const _pendingRewards = await earnedBalanceCallback()
      setEarned(_pendingRewards)
    } catch (error: any) {
      console.error(error)
    }
  }

  const fetchTVL = async () => {
    try {
      const _tokenPrice = await dtoPriceCallback()
      const _totalSupply = await totalSupplyCallback()
      if (pool.id === 0) {
        setTVL(_totalSupply * _tokenPrice)
      } else {
        const _tvl = await tvlCallback()
        setTVL(_tvl)
      }
    } catch (error: any) {
      console.error(error)
    }
  }

  const fetchLockInfo = async () => {
    try {
      if (account && rewardCallback) {
        const _lockInfoLength = await rewardCallback.methods.getLockInfoLength(account).call()
        const _lockInfo = await rewardCallback.methods.getLockInfo(account).call()
        const lockInfoArray: LockInfo[] = []

        for (let i = 0; i < Number(_lockInfoLength); i++) {
          const info = {
            index: i,
            isWithdrawn: _lockInfo['isWithdrawns'][i] as boolean,
            token: _lockInfo['tokens'][i],
            unlockableAt: Number(_lockInfo['unlockableAts'][i]),
            amount: fromWei(_lockInfo['amounts'][i]).toNumber(),
          } as LockInfo

          if (!info.isWithdrawn) {
            lockInfoArray.push(info)
          }
        }

        setLockInfo(lockInfoArray.reverse())
      }
    } catch (error: any) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchPendingRewards()
    fetchLockInfo()

    const interval = setInterval(() => {
      fetchPendingRewards()
    }, 1000 * 10)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account])

  useEffect(() => {
    fetchTVL()
    fetchStakedBalance()
  }, [account])

  const onRefresh = async () => {
    await fetchTVL()
    await fetchStakedBalance()
    await fetchLockInfo()
    await fetchPendingRewards()
  }

  const onApproveToken = async () => {
    try {
      setLoading(true)
      const receipt = await approveCallback()

      if (receipt && tokenContract) {
        toast.success(<ToastMessage color="success" headerText="Success!" bodyText="Now you can stake your tokens" />, {
          toastId: 'onApprove',
        })
        setNeedApprove(false)
      }
    } catch (error: any) {
      // we only care if the error is something _other_ than the user rejected the tx
      if (error?.code !== 4001) {
        toast.error(
          <ToastMessage color="error" headerText="Error!" bodyText="Could not approve. Please try again." />,
          {
            toastId: 'onApprove',
          },
        )
      }
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onClaimRewards = async () => {
    try {
      setClaiming(true)
      let receipt: any = {}

      if (rewardCallback) {
        receipt = await rewardCallback.methods.getReward().send({ from: account })
      }

      if (receipt) {
        await onRefresh()

        toast.success(
          <ToastMessage
            color="success"
            headerText="Success!"
            bodyText=""
            link={`${process.env.REACT_APP_EXPLORER_URL}/tx/${receipt.transactionHash}`}
            linkText="View Transaction"
          />,
          {
            toastId: 'onClaimRewards',
          },
        )
      }
    } catch (error: any) {
      // we only care if the error is something _other_ than the user rejected the tx
      if (error?.code !== 4001) {
        toast.error(
          <ToastMessage color="error" headerText="Error!" bodyText="Could not claim rewards. Please try again." />,
          {
            toastId: 'onClaimRewards',
          },
        )
      }
      console.error(error)
    } finally {
      setClaiming(false)
    }
  }

  return (
    <Container>
      <Row className="justify-content-center text-center">
        <Col>
          <Link to="/" className="d-block mt-3">
            <BiChevronsLeft size={16} />
            Back
          </Link>
        </Col>
      </Row>
      <Row>
        <Col className="text-center">
          <TotalValueLockedH4>
            Total Value Locked: <span>{formatNumber(tvl.toFixed(3))}</span> USD
          </TotalValueLockedH4>
        </Col>
      </Row>
      <Row className="pt-4 justify-content-center">
        <StakeCol md={6} xl={3}>
          <StakeWrapperFlex>
            <Icons>
              <Icon src={ParaLogo} alt="Paradiso" />
            </Icons>
            <Value value={earned} />
            <StakeDescription>DTO Earned</StakeDescription>
            <StakeButtons>
              <Button
                onClick={onClaimRewards}
                color="secondary"
                disabled={earned <= 0 || isClaiming}
                loading={isClaiming}
              >
                Claim Rewards
              </Button>
            </StakeButtons>
          </StakeWrapperFlex>
        </StakeCol>
        <StakeCol md={6} xl={3}>
          <StakeWrapperFlex>
            <Icons>
              <Icon src={ParaLogo} alt="Paradiso" />
            </Icons>
            <StakeNumber>{formatNumber(staked.toFixed(3))}</StakeNumber>
            <StakeDescription>{pool.symbol} Tokens Staked</StakeDescription>
            <ShareOfPool>Share of Pool: {formatNumber(shareOfPool.toFixed(3))} %</ShareOfPool>
            {needApprove && approval !== ApprovalState.APPROVED ? (
              <StakeButtons>
                <Button
                  color="secondary"
                  disabled={isLoading}
                  loading={isLoading}
                  onClick={onApproveToken}
                >{`Approve ${pool.symbol}`}</Button>
              </StakeButtons>
            ) : (
              <ButtonFlex>
                <Button color="primary" onClick={() => setShowStakeModal(true)}>
                  Stake
                </Button>
                <Button color="secondary" onClick={() => setShowUnstakeModal(true)}>
                  -
                </Button>
              </ButtonFlex>
            )}
            <StakeModal
              show={showStakeModal}
              onHide={() => setShowStakeModal(false)}
              tokenName={pool.symbol}
              tokenAddress={pool.lpAddress}
              poolId={pool.id}
              version={pool.version}
              onRefresh={onRefresh}
            />
            <UnstakeModal
              show={showUnstakeModal}
              onHide={() => setShowUnstakeModal(false)}
              tokenName={pool.symbol}
              poolId={pool.id}
              version={pool.version}
              onRefresh={onRefresh}
            />
          </StakeWrapperFlex>
        </StakeCol>
        <StakeCol md={6} xl={3}>
          <StakeWrapperFlex>
            <h5 className="mb-3 text-center">Withdrawal Requests</h5>
            <WithdrawHeading>
              <WithdrawAmount>Amount</WithdrawAmount>
              <WithdrawAction>&nbsp;</WithdrawAction>
            </WithdrawHeading>
            {lockInfo.length > 0 ? (
              <WithdrawDiv>
                {lockInfo.map((info, index) => {
                  return <WithdrawRow key={index} lockInfo={info} onRefresh={onRefresh} />
                })}
              </WithdrawDiv>
            ) : (
              <p style={{ color: '#727eaa' }}>There are no withdrawal requests.</p>
            )}
          </StakeWrapperFlex>
        </StakeCol>
      </Row>
    </Container>
  )
}

export default SingleStake
